"use client";

import { useRef, useState } from "react";
import { DEFAULT_ACCENT, type Brand } from "@/lib/brand";
import { track } from "@/lib/analytics";

/** A solid logo background colour (from the four corners), or "" if the corners are
 *  transparent or not uniform — used to auto-match the header so the logo sits flush. */
function detectBackground(ctx: CanvasRenderingContext2D, w: number, h: number): string {
  try {
    const corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]].map(([x, y]) => ctx.getImageData(x, y, 1, 1).data);
    if (corners.some((p) => p[3] < 200)) return ""; // a transparent corner → blends already
    const avg = [0, 1, 2].map((i) => Math.round(corners.reduce((s, p) => s + p[i], 0) / corners.length));
    const uniform = corners.every((p) => Math.abs(p[0] - avg[0]) < 14 && Math.abs(p[1] - avg[1]) < 14 && Math.abs(p[2] - avg[2]) < 14);
    return uniform ? "#" + avg.map((v) => v.toString(16).padStart(2, "0")).join("") : "";
  } catch {
    return ""; // tainted canvas / read blocked — skip auto-match
  }
}

/** Optional logo + accent colour for the RAMS. Logos are downscaled to a small
 *  PNG in the browser so the embedded data URI stays safe (raster, no script) and
 *  small enough for the PDF. Applies live to the preview and the downloaded PDF. */
export function BrandingControls({ brand, onChange }: { brand: Brand; onChange: (b: Brand) => void }) {
  const [err, setErr] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  function pickLogo(file: File) {
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      setErr("Use a PNG, JPG or WebP image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Downscale to a header-sized strip; keeps the data URI small and crisp.
        const maxH = 96;
        const scale = Math.min(1, maxH / img.height);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          setErr("Couldn't process that image.");
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const out = canvas.toDataURL("image/png");
        if (out.length > 180000) {
          setErr("That logo is too detailed — try a simpler or smaller image.");
          return;
        }
        setErr("");
        // Match the header to a solid logo background (white-bg logos are common),
        // so the logo sits flush instead of as a box on the navy band.
        const bg = detectBackground(ctx, w, h);
        onChange({ ...brand, logo: out, ...(bg ? { headerColor: bg } : {}) });
        track("brand_logo_added", { auto_header: !!bg });
      };
      img.onerror = () => setErr("Couldn't read that image.");
      img.src = String(reader.result);
    };
    reader.onerror = () => setErr("Couldn't read that file.");
    reader.readAsDataURL(file);
  }

  const hasBranding = !!brand.logo || !!brand.color || !!brand.headerColor;

  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[0.82rem] font-semibold text-ink">Brand this document <span className="font-normal text-muted">(optional)</span></p>
          <p className="mt-0.5 text-[0.72rem] text-muted">Add your logo and colour — they appear on the preview and the PDF.</p>
        </div>
        {hasBranding && (
          <button
            type="button"
            onClick={() => { onChange({ logo: "", color: "", headerColor: "" }); setErr(""); if (fileRef.current) fileRef.current.value = ""; }}
            className="shrink-0 text-[0.72rem] font-semibold text-muted underline-offset-2 hover:text-ink hover:underline"
          >
            Reset
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          {brand.logo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={brand.logo} alt="Your logo" className="h-9 w-auto max-w-[120px] rounded border border-line bg-white object-contain p-1" />
          ) : (
            <span className="flex h-9 w-16 items-center justify-center rounded border border-dashed border-line text-[0.62rem] text-muted">Logo</span>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="rounded-lg border border-line px-3 py-2 text-[0.76rem] font-semibold text-ink transition-colors hover:bg-mist"
          >
            {brand.logo ? "Change logo" : "Upload logo"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) pickLogo(f); }}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-[0.76rem] font-semibold text-ink">
          Header
          <input
            type="color"
            value={brand.headerColor || "#0b0c2a"}
            onChange={(e) => onChange({ ...brand, headerColor: e.target.value })}
            className="h-8 w-10 cursor-pointer rounded border border-line bg-white p-0.5"
            aria-label="Header colour"
          />
        </label>

        <label className="flex items-center gap-2 text-[0.76rem] font-semibold text-ink">
          Accent
          <input
            type="color"
            value={brand.color || DEFAULT_ACCENT}
            onChange={(e) => onChange({ ...brand, color: e.target.value })}
            className="h-8 w-10 cursor-pointer rounded border border-line bg-white p-0.5"
            aria-label="Accent colour"
          />
        </label>
      </div>

      {err && <p role="alert" className="mt-2 text-[0.72rem] text-risk-veryhigh">{err}</p>}
    </div>
  );
}
